import { useEffect, useState } from "react";
import {
  useAuth, useTickers, LoadingSkeleton, DenialScreen, Banner, ErrorBoundary,
  SignInScreen, SignOutLink, isEmbeddedInDiscord,
} from "@2skies/frontend-shared";
import { useTapeStream } from "./hooks/useTapeStream.js";
import { getMarketRegime } from "./state/marketHours.js";
import { classifyAge } from "./state/freshness.js";
import { DeltaTapePane } from "./components/DeltaTapePane.js";

const DELTATAPE_CLIENT_ID = import.meta.env.VITE_DELTATAPE_DISCORD_CLIENT_ID as string;
const BROWSER_URL = (import.meta.env.VITE_BROWSER_URL as string | undefined) ?? "";
const DEFAULT_SYMBOL = "SPY";

export function App(): JSX.Element {
  const auth = useAuth("/api/deltatape", DELTATAPE_CLIENT_ID, { browserUrl: BROWSER_URL });
  const authorized = auth.status === "authorized";

  if (auth.status === "loading") return <LoadingSkeleton />;
  if (auth.status === "signed-out") {
    return (
      <SignInScreen
        title="DeltaTape"
        appPath="/api/deltatape"
        clientId={DELTATAPE_CLIENT_ID}
        browserUrl={BROWSER_URL}
        canSignIn={auth.canSignIn}
        {...(auth.message ? { message: auth.message } : {})}
      />
    );
  }
  // In browser mode a wrong-account member must be able to sign out from here.
  const signOut = !isEmbeddedInDiscord() && BROWSER_URL ? (
    <div className="signout-standalone"><SignOutLink appPath="/api/deltatape" /></div>
  ) : null;
  if (auth.status === "denied") {
    return (
      <>
        <DenialScreen
          title="DeltaTape"
          message="DeltaTape is included in the Gold tier. If you've just upgraded, wait a few minutes and reopen."
        />
        {signOut}
      </>
    );
  }
  if (auth.status === "error") {
    return (
      <>
        <Banner variant="broken">Could not connect: {auth.message}</Banner>
        {signOut}
      </>
    );
  }

  return (
    <ErrorBoundary>
      <DeltaTapeApp authorized={authorized} username={auth.username} />
    </ErrorBoundary>
  );
}

function DeltaTapeApp({ authorized, username }: { authorized: boolean; username: string }): JSX.Element {
  const token = authorized ? (window as unknown as { __token?: string }).__token ?? null : null;
  const tickers = useTickers(authorized);
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
  const [show0dte, setShow0dte] = useState(false);
  const stream = useTapeStream(token, symbol);

  // Re-classify freshness on a 1s tick so the footer ages without new data.
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const symbols = tickers?.tickers.map((t) => t.symbol) ?? [symbol];
  const marketClosed = getMarketRegime() !== "regular";

  const ageSec = stream.lastUpdateMs === null ? Infinity : (Date.now() - stream.lastUpdateMs) / 1000;
  const staleness = classifyAge(ageSec);
  const freshnessLabel =
    stream.connection === "paused"
      ? `${symbol} · paused`
      : stream.connection === "open" && staleness === "live"
        ? `Live · ${symbol}`
        : stream.lastUpdateMs === null
          ? `${symbol} · waiting for data`
          : `${symbol} · as of ${formatEtClock(stream.lastUpdateMs)}`;
  const freshnessStale =
    staleness === "stale" &&
    !marketClosed &&
    stream.connection !== "displaced" &&
    stream.connection !== "paused";

  return (
    <DeltaTapePane
      symbols={symbols}
      symbol={symbol}
      onSymbol={setSymbol}
      username={username}
      show0dte={show0dte}
      onToggle0dte={setShow0dte}
      snapshot={stream.snapshot}
      connection={stream.connection}
      marketClosed={marketClosed}
      freshnessLabel={freshnessLabel}
      freshnessStale={freshnessStale}
      embedded={isEmbeddedInDiscord()}
      browserUrl={BROWSER_URL}
      onReconnect={stream.reconnect}
    />
  );
}

function formatEtClock(ms: number): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(ms)) + " ET";
}
